import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../utils/cn';
import { Cat } from '@phosphor-icons/react';
import { House } from '../../components/icons/house';
import { Package as AnimatedPackage } from '../../components/icons/package';
import { ShoppingBag as AnimatedShoppingBag } from '../../components/icons/shopping-bag';
import { Heart as AnimatedHeart } from '../../components/icons/heart';
import { List as AnimatedList } from '../../components/icons/list';
import { X as AnimatedX } from '../../components/icons/x';
import { SignOut as AnimatedSignOut } from '../../components/icons/sign-out';
import { User, Info, MagnifyingGlass } from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import { useCartStore } from '../../store/cartStore';
import { useFavoritesStore } from '../../store/favoritesStore';
import { useI18n } from '../../context/I18nContext';
import { LanguageToggle } from '../ui/LanguageToggle';
import { ThemeToggle } from '../ui/ThemeToggle';
import { AnimatedSocialIcon } from '../icons/animated-social-icons';

export const Header: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { t, language } = useI18n();
  const cartCount = useCartStore((state) => state.getTotalItems());
  const favCount = useFavoritesStore((state) => state.getCount());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const cartBagRef = useRef<any>(null);
  const cartBagMobileRef = useRef<any>(null);
  const prevCartCount = useRef(cartCount);

  useEffect(() => {
    if (cartCount > prevCartCount.current && cartBagRef.current) {
      cartBagRef.current.play();
    }
    if (cartCount > prevCartCount.current && cartBagMobileRef.current) {
      cartBagMobileRef.current.play();
    }
    prevCartCount.current = cartCount;
  }, [cartCount]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { path: '/', label: t('nav.home'), icon: House },
    { path: '/cats', label: t('nav.cats'), icon: Cat },
    { path: '/products', label: t('nav.products'), icon: AnimatedPackage },
  ];

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-40 transition-all duration-300',
        isScrolled
          ? 'bg-white/95 dark:bg-base-900/95 backdrop-blur-xl shadow-sm border-b border-base-200 dark:border-base-800'
          : 'bg-white/80 dark:bg-base-900/80 backdrop-blur-md'
      )}
      role="banner"
    >
      <nav className="container-page" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16 lg:h-18">
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 text-heading-lg font-bold text-base-900 dark:text-base-100 z-50"
            aria-label={t('nav.home')}
          >
            <img src="/logo.png" alt="CAT CARE" className="w-8 h-8 lg:w-9 lg:h-9 object-contain" />
            <span className="tracking-tighter">CAT CARE</span>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 text-body-md font-medium rounded-xl transition-all duration-150',
                  location.pathname === item.path
                    ? 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300'
                    : 'text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100'
                )}
              >
                <item.icon size={18} trigger="hover" weight="regular" />
                {item.label}
              </Link>
            ))}
          </div>

          {/* Desktop actions */}
          <div className="hidden lg:flex items-center gap-2">
            <LanguageToggle />

            <ThemeToggle />

            <Link
              to="/favorites"
              className={cn(
                'relative p-2.5 rounded-xl text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100 transition-all duration-150',
                location.pathname === '/favorites' && 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
              )}
              aria-label={t('favorites.title')}
            >
              <AnimatedHeart size={22} trigger="hover" weight={favCount > 0 ? 'duotone' : 'regular'} className={favCount > 0 ? 'text-red-500' : ''} />
              {favCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {favCount}
                </span>
              )}
            </Link>

            <Link
              to="/my-requests"
              className={cn(
                'relative p-2.5 rounded-xl text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100 transition-all duration-150',
                location.pathname === '/my-requests' && 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300'
              )}
              aria-label={language === 'ar' ? 'طلباتي' : 'My Requests'}
            >
              <MagnifyingGlass size={22} weight="regular" />
            </Link>

            <Link
              to="/cart"
              className={cn(
                'relative p-2.5 rounded-xl text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100 transition-all duration-150',
                location.pathname === '/cart' && 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300'
              )}
              aria-label={t('nav.cart')}
            >
              <AnimatedShoppingBag ref={cartBagRef} size={22} trigger="click" weight="regular" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-accent-600 text-white text-[10px] font-bold flex items-center justify-center animate-pop">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>

            {isAuthenticated && (
              <div className="relative">
                <button
                  className="flex items-center gap-2 px-4 py-2 text-body-md font-medium text-base-600 dark:text-base-400 hover:text-base-900 dark:hover:text-base-100 hover:bg-base-100 dark:hover:bg-base-800 rounded-xl transition-all duration-150"
                  aria-expanded={isMenuOpen}
                  aria-haspopup="true"
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                  <User size={20} weight="regular" />
                  <span className="hidden sm:inline">{user?.email?.split('@')[0] || t('nav.profile')}</span>
                </button>

                {isMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsMenuOpen(false)} aria-hidden="true" />
                    <div className="absolute right-0 top-full mt-2 w-56 glass-panel rounded-2xl shadow-xl py-2 animate-slide-down z-50">
                      <Link to="/profile" className="flex items-center gap-3 px-4 py-2 text-body-md text-base-700 dark:text-base-300 hover:bg-base-100 dark:hover:bg-base-800" onClick={() => setIsMenuOpen(false)}>
                        <User size={18} weight="regular" />
                        {t('nav.profile')}
                      </Link>
                      <Link to="/wishlist" className="flex items-center gap-3 px-4 py-2 text-body-md text-base-700 dark:text-base-300 hover:bg-base-100 dark:hover:bg-base-800" onClick={() => setIsMenuOpen(false)}>
                        <AnimatedHeart size={18} trigger="click" weight="regular" />
                        {t('nav.wishlist')}
                      </Link>
                      <hr className="my-2 border-base-200 dark:border-base-700" />
                      <button
                        onClick={() => { logout(); setIsMenuOpen(false); }}
                        className="flex items-center gap-3 w-full px-4 py-2 text-body-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <AnimatedSignOut size={18} trigger="click" weight="regular" />
                        {t('nav.logout')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Mobile actions */}
          <div className="flex lg:hidden items-center gap-1.5">
            <LanguageToggle />

            <ThemeToggle />

            <Link
              to="/cart"
              className={cn(
                'relative p-2 rounded-xl text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 transition-all duration-150',
                location.pathname === '/cart' && 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300'
              )}
              aria-label={t('nav.cart')}
            >
              <AnimatedShoppingBag ref={cartBagMobileRef} size={20} trigger="click" weight="regular" />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-accent-600 text-white text-[9px] font-bold flex items-center justify-center animate-pop">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              )}
            </Link>

            <button
              className="p-2 rounded-xl text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 transition-all duration-150"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            >
              {isMenuOpen ? <AnimatedX size={22} trigger="click" weight="bold" /> : <AnimatedList size={22} trigger="click" weight="regular" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {isMenuOpen && (
          <div id="mobile-menu" className="lg:hidden rounded-2xl bg-white dark:bg-base-900 border border-base-200 dark:border-base-700 animate-slide-down overflow-hidden mb-4 shadow-xl">
            <div className="px-4 py-4 space-y-1">
              {/* Navigation */}
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    'flex items-center gap-3 px-3 py-3 rounded-xl text-body-md font-medium transition-all duration-150',
                    location.pathname === item.path
                      ? 'bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300'
                      : 'text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100'
                  )}
                >
                  <item.icon size={20} trigger="loop" weight="regular" />
                  {item.label}
                </Link>
              ))}

              <hr className="my-3 border-base-200 dark:border-base-700" />

              {/* Actions */}
              <Link
                to="/favorites"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-body-md font-medium text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100"
              >
                <AnimatedHeart size={20} trigger="loop" weight={favCount > 0 ? 'duotone' : 'regular'} className={favCount > 0 ? 'text-red-500' : ''} />
                {t('favorites.title')}
                {favCount > 0 && (
                  <span className="ml-auto w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{favCount}</span>
                )}
              </Link>

              <Link
                to="/cart"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-body-md font-medium text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100"
              >
                <AnimatedShoppingBag size={20} trigger="loop" weight="regular" />
                {t('nav.cart')}
                {cartCount > 0 && (
                  <span className="ml-auto w-5 h-5 rounded-full bg-accent-600 text-white text-[10px] font-bold flex items-center justify-center animate-pop">{cartCount}</span>
                )}
              </Link>

              <Link
                to="/my-requests"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-body-md font-medium text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100"
              >
                <MagnifyingGlass size={20} weight="regular" />
                {language === 'ar' ? 'طلباتي' : 'My Requests'}
              </Link>

              <hr className="my-3 border-base-200 dark:border-base-700" />

              {/* About */}
              <Link
                to="/about"
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-body-md font-medium text-base-600 dark:text-base-400 hover:bg-base-100 dark:hover:bg-base-800 hover:text-base-900 dark:hover:text-base-100"
              >
                <Info size={20} weight="regular" />
                {t('nav.about')}
              </Link>

              <hr className="my-3 border-base-200 dark:border-base-700" />

              {/* Social icons */}
              <div className="flex items-center justify-center gap-3 pt-2">
                <AnimatedSocialIcon name="whatsapp" href="https://wa.me/+201025400119" label="WhatsApp" size={20} trigger="mount" />
                <AnimatedSocialIcon name="facebook" href="https://www.facebook.com/profile.php?id=61583695298740" label="Facebook" size={20} trigger="mount" />
                <AnimatedSocialIcon name="instagram" href="https://www.instagram.com/catcare98" label="Instagram" size={20} trigger="mount" />
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
